import React, { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { Image } from 'expo-image';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import VideoPreviewPlayer from './VideoPreviewPlayer';
import { displayStillLabel, seedVideoRedoPrompt } from '@/constants/utils/video_project';

/** A new version starts with the change the user wants, using the existing brief. */
export default function VideoProjectRedoSheet({
  visible, item, kind, previewUrl, colors, busy, onClose, onSubmit,
}) {
  const [prompt, setPrompt] = useState('');
  const [notes, setNotes] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const sheetRef = useRef(null);
  const notesRef = useRef(null);
  const submittingRef = useRef(false);
  const closeRef = useRef(onClose);
  const busyRef = useRef(busy || submitting);
  closeRef.current = onClose;
  busyRef.current = busy || submitting;
  const pending = busy || submitting;

  useEffect(() => {
    if (!visible || !item) return;
    setPrompt(seedVideoRedoPrompt(item));
    setNotes('');
    setShowPrompt(false);
    setError('');
  }, [visible, item]);

  useEffect(() => {
    if (!visible || Platform.OS !== 'web') return undefined;
    const previousFocus = document.activeElement;
    notesRef.current?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (!busyRef.current) closeRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const sheet = sheetRef.current;
      const focusable = Array.from(sheet?.querySelectorAll(
        'button, textarea, input, select, a[href], [tabindex]:not([tabindex="-1"])',
      ) || []).filter((element) => (
        !element.disabled
        && element.getAttribute('aria-disabled') !== 'true'
        && !element.closest('[aria-hidden="true"]')
      ));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) {
        event.preventDefault();
        sheet?.focus();
      } else if (event.shiftKey && (document.activeElement === first || !sheet.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !sheet.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (previousFocus?.isConnected) previousFocus.focus?.();
    };
  }, [visible]);

  if (!visible || !item) return null;

  const label = kind === 'still' ? displayStillLabel(item) : `shot ${item.index ?? ''}`;
  const suggestions = [
    { label: 'Warmer lighting', direction: 'Make the lighting warmer.' },
    kind === 'still'
      ? { label: 'Clearer expression', direction: 'Make the facial expression clearer.' }
      : { label: 'Smoother camera', direction: 'Use smoother, slower camera movement.' },
    { label: 'Keep character consistent', direction: 'Keep the character’s face, clothing, and appearance consistent with the reference images.' },
  ];
  const fieldStyle = {
    color: colors.text_color_primary,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_secondary,
  };
  const close = () => { if (!pending) onClose?.(); };
  const submit = async () => {
    if (busy || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      const sent = await onSubmit?.({ prompt, notes });
      if (sent === false) setError('Could not create a new version. Your changes are still here. Please try again.');
    } catch (err) {
      setError(err?.message || 'Could not create a new version. Your changes are still here. Please try again.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <View style={styles.scrim} accessibilityViewIsModal>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Close redo"
        disabled={pending}
        onPress={close}
        tabIndex={-1}
        style={styles.backdrop}
      />
      <View
        ref={sheetRef}
        accessibilityRole="dialog"
        accessibilityLabel={`Create a new version of ${label}`}
        aria-modal="true"
        tabIndex={-1}
        style={[styles.sheet, { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_secondary }]}
      >
        <View style={[styles.header, { borderBottomColor: colors.border_color_secondary }]}>
          <View style={styles.headerCopy}>
            <Text size="m" weight="semibold">Improve {label}</Text>
            <Text size="xs" color="secondary">Describe a change. We’ll create another version for you to compare.</Text>
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close redo"
            disabled={pending}
            onPress={close}
            style={({ hovered }) => [styles.close, hovered && { backgroundColor: colors.solid_color_secondary }, pending && styles.disabled]}
          >
            <Icon name="xmark" size={16} color={colors.text_color_secondary} />
          </Pressable>
        </View>

        <ScrollView style={styles.scroll} contentContainerStyle={styles.body} keyboardShouldPersistTaps="handled">
          {previewUrl ? (
            <View style={styles.previewRow}>
              <View style={[styles.preview, Platform.OS === 'macos' && kind !== 'still' && { width: 180, height: 160 }]}>
                {kind === 'still' ? (
                  <Image source={{ uri: previewUrl }} style={styles.previewMedia} contentFit="contain" />
                ) : Platform.OS === 'web' ? (
                  // eslint-disable-next-line react/forbid-elements
                  <video src={previewUrl} controls playsInline preload="metadata" aria-label={`Current version of ${label}`}
                    style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain', backgroundColor: '#0B0B0D' }} />
                ) : <VideoPreviewPlayer key={previewUrl} url={previewUrl} compact title={`Current version of ${label}`} />}
              </View>
              <View style={styles.previewCopy}>
                <Text size="xs" weight="semibold">Current version</Text>
                <Text size="xs" color="secondary">Previous takes stay on the timeline so you can compare and choose your favorite.</Text>
              </View>
            </View>
          ) : (
            <Text size="xs" color="secondary">Your current version stays available. You can compare versions and choose your favorite.</Text>
          )}

          <View style={styles.field}>
            <Text size="s" weight="semibold">What would you like to change?</Text>
            <Text size="xs" color="secondary">Use everyday words. The rest of the scene will use the original brief.</Text>
            <TextInput
              ref={notesRef}
              accessibilityLabel="Redo notes"
              accessibilityHint="Describe what to change in the new version. The original brief is preserved below."
              value={notes}
              onChangeText={(value) => { setNotes(value); setError(''); }}
              editable={!pending}
              multiline
              placeholder={kind === 'still' ? 'e.g. Make her smile and use softer afternoon light.' : 'e.g. Slow down the camera and have him wave at the end.'}
              placeholderTextColor={colors.text_color_tertiary}
              style={[styles.input, styles.notesInput, fieldStyle]}
            />
          </View>

          <View style={styles.field}>
            <Text size="xs" color="secondary">Try a change</Text>
            <View style={styles.suggestions}>
              {suggestions.map((suggestion) => (
                <Pressable
                  key={suggestion.label}
                  accessibilityRole="button"
                  accessibilityLabel={`Add suggestion: ${suggestion.label}`}
                  disabled={pending}
                  onPress={() => {
                    setNotes((current) => current.includes(suggestion.direction)
                      ? current
                      : [current.trim(), suggestion.direction].filter(Boolean).join('\n'));
                    setError('');
                    notesRef.current?.focus();
                  }}
                  style={({ hovered }) => [styles.suggestion, { borderColor: colors.border_color_secondary }, hovered && { backgroundColor: colors.solid_color_secondary }, pending && styles.disabled]}
                >
                  <Icon name="plus" size={10} color={colors.text_color_secondary} />
                  <Text size="xs" color="secondary">{suggestion.label}</Text>
                </Pressable>
              ))}
            </View>
          </View>

          <View style={[styles.original, { borderColor: colors.border_color_secondary }]}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Edit original prompt"
              accessibilityState={{ expanded: showPrompt }}
              disabled={pending}
              onPress={() => setShowPrompt((current) => !current)}
              style={styles.originalToggle}
            >
              <View style={styles.headerCopy}>
                <Text size="xs" weight="semibold">Original scene description</Text>
                <Text size="xs" color="tertiary">Optional · edit the full brief</Text>
              </View>
              <Icon name={showPrompt ? 'chevron-up' : 'chevron-down'} size={12} color={colors.text_color_secondary} />
            </Pressable>
            <View aria-hidden={!showPrompt} style={[styles.originalBody, !showPrompt && styles.hidden]}>
              <TextInput
                accessibilityLabel="Redo prompt"
                value={prompt}
                onChangeText={(value) => { setPrompt(value); setError(''); }}
                editable={!pending}
                multiline
                style={[styles.input, styles.promptInput, fieldStyle]}
              />
            </View>
          </View>
          {error ? <Text size="xs" accessibilityRole="alert" style={{ color: colors.text_color_primary }}>{error}</Text> : null}
        </ScrollView>

        <View style={[styles.footer, { borderTopColor: colors.border_color_secondary }]}>
          <Text size="xs" color="secondary" style={styles.footerHint} accessibilityLiveRegion="polite">
            {pending ? 'Requesting your new version…' : 'Creates a new version. Keeps your original.'}
          </Text>
          <View style={styles.actions}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Cancel redo"
              disabled={pending}
              onPress={close}
              style={({ hovered }) => [styles.cancel, { borderColor: colors.border_color_secondary }, hovered && { backgroundColor: colors.solid_color_secondary }, pending && styles.disabled]}
            >
              <Text size="xs" weight="medium">Cancel</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Generate retake"
              accessibilityState={{ disabled: pending, busy: pending }}
              disabled={pending}
              onPress={submit}
              style={({ hovered }) => [styles.submit, { backgroundColor: colors.text_color_primary }, hovered && { opacity: 0.9 }, pending && styles.disabled]}
            >
              <Icon name="arrow-rotate-right" size={13} color={colors.solid_color_primary} />
              <Text size="xs" weight="semibold" style={{ color: colors.solid_color_primary }}>
                {pending ? 'Requesting…' : 'Create new version'}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  scrim: { ...StyleSheet.absoluteFillObject, zIndex: 8, justifyContent: 'center', alignItems: 'center', padding: 12 },
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.56)' },
  sheet: { width: '100%', maxWidth: 560, maxHeight: '96%', borderWidth: 1, borderRadius: 18, overflow: 'hidden', zIndex: 9,
    ...(Platform.OS === 'web' ? { boxShadow: '0 22px 56px rgba(0,0,0,0.42)' } : {}) },
  header: { flexDirection: 'row', alignItems: 'flex-start', padding: 18, gap: 10, borderBottomWidth: 1 },
  headerCopy: { flex: 1, minWidth: 0, gap: 5 },
  close: { width: 40, height: 40, borderRadius: 10, alignItems: 'center', justifyContent: 'center', marginTop: -8, marginRight: -8 },
  scroll: { flexShrink: 1, minHeight: 0 },
  body: { padding: 18, gap: 18 },
  previewRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  preview: { width: 128, height: 84, borderRadius: 10, overflow: 'hidden', backgroundColor: '#0B0B0D', alignItems: 'center', justifyContent: 'center' },
  previewMedia: { width: '100%', height: '100%' },
  previewCopy: { flex: 1, minWidth: 0, gap: 5 },
  field: { gap: 8 },
  input: { borderWidth: 1, borderRadius: 10, padding: 12, fontSize: 14, lineHeight: 21, textAlignVertical: 'top' },
  notesInput: { minHeight: 112 },
  promptInput: { minHeight: 144 },
  suggestions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  suggestion: { minHeight: 36, borderWidth: 1, borderRadius: 9, paddingHorizontal: 10, paddingVertical: 7, flexDirection: 'row', alignItems: 'center', gap: 6 },
  original: { borderWidth: 1, borderRadius: 10 },
  originalToggle: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, minHeight: 52 },
  originalBody: { paddingHorizontal: 12, paddingBottom: 12 },
  hidden: { display: 'none' },
  footer: { borderTopWidth: 1, padding: 16, gap: 10 },
  footerHint: { textAlign: 'center' },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8 },
  cancel: { minHeight: 42, paddingHorizontal: 16, borderWidth: 1, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  submit: { minHeight: 42, paddingHorizontal: 16, borderRadius: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  disabled: { opacity: 0.55 },
});
